<template>
  <!-- The inspection is created under the PROVIDER's ADAS Map firm, so without this there is nothing
       to say which shop it came from. Picking a saved shop fills it in; a new name is created on submit. -->
  <div class="custom-shop-section">
    <button type="button" class="custom-section-header" @click="open = !open">
      <i class="fas" :class="open ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
      Customer Shop <span class="text-danger">*</span><span v-if="modelValue.shopName" class="fw-normal"> ({{ modelValue.shopName }})</span>
      <span class="custom-section-hint">{{ open ? 'click to collapse' : 'click to expand' }}</span>
    </button>
    <div v-show="open" class="custom-shop-body">
      <div class="row g-2">
        <!-- The saved shops (Locations > Serving > Non-ADAS Map Shops) as an outright choice; manual
             entry stays available underneath. -->
        <div v-if="shops.length" class="col-12">
          <label class="custom-label">Saved Shop</label>
          <select class="form-control form-control-sm" :value="modelValue.customerShop || ''" :disabled="disabled"
            @change="onShopSelect($event.target.value)">
            <option value="">enter manually</option>
            <option v-for="s in shops" :key="s.customerShop" :value="s.customerShop">
              {{ s.shopName }}{{ s.city ? ` - ${s.city}` : '' }}
            </option>
          </select>
        </div>
        <div class="col-12">
          <label class="custom-label">Shop Name <span class="text-danger">*</span></label>
          <input class="form-control form-control-sm" list="customerShopList" :value="modelValue.shopName"
            :class="{ 'is-invalid': nameMissing }" placeholder="shop name" :disabled="disabled"
            @input="set('shopName', $event.target.value)" @change="onShopNameChange" />
          <datalist id="customerShopList">
            <option v-for="s in shops" :key="s.customerShop" :value="s.shopName" />
          </datalist>
        </div>
        <div class="col-6">
          <label class="custom-label">Contact Name</label>
          <input class="form-control form-control-sm" :value="modelValue.contactName" :disabled="disabled"
            @input="set('contactName', $event.target.value)" />
        </div>
        <div class="col-6">
          <label class="custom-label">Email</label>
          <input class="form-control form-control-sm" :class="{ 'is-invalid': contactMissing }" type="email"
            :value="modelValue.email" placeholder="customer email" :disabled="disabled"
            @input="set('email', $event.target.value)" />
        </div>
        <div class="col-4">
          <label class="custom-label">Phone</label>
          <input class="form-control form-control-sm" :class="{ 'is-invalid': contactMissing }" :value="modelValue.phone"
            :disabled="disabled" @input="set('phone', $event.target.value)" />
        </div>
        <div class="col-8">
          <label class="custom-label">Address</label>
          <input class="form-control form-control-sm" :value="modelValue.address1" :disabled="disabled"
            @input="set('address1', $event.target.value)" />
        </div>
        <div class="col-5">
          <label class="custom-label">City</label>
          <input class="form-control form-control-sm" :value="modelValue.city" :disabled="disabled"
            @input="set('city', $event.target.value)" />
        </div>
        <div class="col-3">
          <label class="custom-label">State</label>
          <input class="form-control form-control-sm" :value="modelValue.stateProvince" :disabled="disabled"
            @input="set('stateProvince', $event.target.value)" />
        </div>
        <div class="col-4">
          <label class="custom-label">Postal Code</label>
          <input class="form-control form-control-sm" :value="modelValue.postalCode" :disabled="disabled"
            @input="set('postalCode', $event.target.value)" />
        </div>
        <div v-if="nameMissing" class="col-12 custom-field-error">
          A customer shop is required, so the inspection records which shop it came from.
        </div>
        <div v-else-if="contactMissing" class="col-12 custom-field-error">
          An email or phone number is required. Without one there is no way to reach the shop about
          this inspection.
        </div>
      </div>
    </div>
  </div>
</template>
<script>
export function emptyShop() {
  return { customerShop: null, shopName: '', contactName: '', email: '', phone: '', address1: '', city: '', stateProvince: '', postalCode: '' }
}
export default {
  name: 'CustomerShopSection',
  props: {
    modelValue: { type: Object, required: true },
    shops: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    nameMissing: { type: Boolean, default: false },
    contactMissing: { type: Boolean, default: false }
  },
  emits: ['update:modelValue'],
  data() {
    return { open: true }
  },
  methods: {
    set(field, value) {
      this.$emit('update:modelValue', { ...this.modelValue, [field]: value })
    },
    // Picking from the list replaces the whole shop block; "enter manually" clears it back out so the
    // fields are not left holding the previous choice's details.
    onShopSelect(value) {
      const id = parseInt(value, 10)
      const match = (this.shops || []).find((s) => parseInt(s.customerShop, 10) === id)
      this.$emit('update:modelValue', match ? { ...emptyShop(), ...match } : emptyShop())
    },
    // Typing a saved shop's exact name fills the rest of its details; a new name is created on submit.
    onShopNameChange() {
      const name = (this.modelValue.shopName || '').trim().toLowerCase()
      const match = (this.shops || []).find((s) => (s.shopName || '').trim().toLowerCase() === name)
      this.$emit('update:modelValue', match ? { ...emptyShop(), ...match } : { ...this.modelValue, customerShop: null })
    }
  }
}
</script>
<style lang="scss" scoped>
// Amber callout: the one block that nothing upstream fills in.
.custom-shop-section {
  border: 1px solid #f0d98c;
  border-radius: 4px;
  margin-top: 0.75rem;
  background-color: #fffbea;
}
.custom-section-header {
  width: 100%;
  text-align: left;
  background: #fdf3c7;
  border: 0;
  border-bottom: 1px solid #f0d98c;
  padding: 0.4rem 0.6rem;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
}
.custom-section-hint { float: right; font-weight: 400; color: #8a949e; }
.custom-shop-body {
  padding: 0.5rem 0.6rem;
}
.custom-label { font-size: 0.75rem; text-transform: uppercase; color: #6c757d; margin-bottom: 2px; }
.custom-field-error {
  font-size: 0.75rem;
  color: #b02a37;
  margin-top: 2px;
}
</style>
